/**
 * MockPlayjectorService - Mock implementation for development/testing
 *
 * Simulates Playjector behavior without requiring Electron or actual game launching.
 * Useful for:
 * - Frontend development in browser
 * - Testing coordinator logic
 * - Remote control dashboard development
 * - CI/CD testing
 *
 * Usage:
 * ```typescript
 * const playjectorService = new MockPlayjectorService();
 * playjectorService.mockGameLaunch(730, 'Counter-Strike 2'); // Simulate game launch
 * playjectorService.mockSteamLogin(); // Simulate Steam login
 * ```
 */

import { PlaycastHostEdition } from '@playcastdotio/CoreTypes';
import type { ProcessLaunchPackageAndroid } from '@playcastdotio/Messaging';
import type {
  IPlayjectorService,
  PlayjectorState,
  SteamCredentials,
  CampaignLaunchResult,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
  MacroStepReply,
  MacroStepTarget,
  ScheduledScreenshotSpec,
  ScheduledScreenshotResult,
  ShadowDisplayAddDisplayResult,
  InstanceCapabilities,
  InstanceLaunchedEvent,
  InstanceClosedEvent,
  RecordingStopResult,
  SessionScreenshotResult,
} from './interfaces/IPlayjectorService';
import { MOCK_SETTINGS, updateMockSettings, resetMockSettings, type MockPlayjectorSettings } from './MockPlayjectorSettings';

interface PendingMacroStep {
  resolve(value: Record<string, MacroStepCaptureResult>): void;
  reject(reason: Error): void;
  captures: Record<string, MacroStepCaptureSpec>;
}

/**
 * Default behavior: synthesize a deterministic 16-char hex hash per captureId.
 * Tests can override per-capture by registering with `registerMacroStepResponse`.
 */
const synthesizeMockHash = (captureId: string): string => {
  // Cheap deterministic 64-bit hash in 16-char lowercase hex form.
  let h1 = 0x12345678;
  let h2 = 0x9abcdef0;
  for (let i = 0; i < captureId.length; i += 1) {
    h1 = ((h1 << 5) - h1 + captureId.charCodeAt(i)) | 0;
    h2 = ((h2 << 7) - h2 + captureId.charCodeAt(i)) | 0;
  }
  const s = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return `${s(h1)}${s(h2)}`;
};

export class MockPlayjectorService implements IPlayjectorService {
  private readonly gameInstanceDisplays = new Map<string, ShadowDisplayAddDisplayResult>();

  public async createVirtualDisplayForSession(
    _hostAuthId: string,
    session: {
      sessionId: string;
      virtualDisplayName: string;
    },
  ): Promise<ShadowDisplayAddDisplayResult> {
    const displayOrdinal = this.gameInstanceDisplays.size + 10;
    const reply: ShadowDisplayAddDisplayResult = {
      success: true,
      sessionId: session.sessionId,
      guid: `{mock-${session.sessionId}}`,
      targetId: displayOrdinal,
      adapterLuid: { lowPart: displayOrdinal, highPart: 0 },
      gdiDeviceName: `\\\\.\\DISPLAY${displayOrdinal}`,
      deviceName: session.virtualDisplayName,
    };
    this.gameInstanceDisplays.set(session.sessionId, reply);
    return reply;
  }

  public notifyShadowDisplayAddDisplayResult(
    reply: ShadowDisplayAddDisplayResult,
  ): boolean {
    if (!reply.sessionId) return false;
    this.gameInstanceDisplays.set(reply.sessionId, reply);
    return true;
  }

  public removeVirtualDisplay(_hostAuthId: string, guid: string): void {
    for (const [sessionId, display] of this.gameInstanceDisplays) {
      if (display.guid === guid) this.gameInstanceDisplays.delete(sessionId);
    }
  }

  public unplugShadowCam(_cameraName: string, _hostAuthId: string): void {}

  private state: PlayjectorState = {
    gameLaunched: false,
    gameName: '',
    gameId: 0,
    steamLaunched: false,
    steamWindow: false,
    userLoggedIn: false,
  };

  private listeners: Array<(state: PlayjectorState) => void> = [];
  private launchResultListeners: Array<(result: CampaignLaunchResult) => void> = [];
  private lastCredentials: SteamCredentials | null = null;
  private gameCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private launchTimer: ReturnType<typeof setTimeout> | null = null;
  private launchTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingCampaignId: string | null = null;

  // ----- Stateful macro step (Phase 3) -----
  private pendingMacroStep: PendingMacroStep | null = null;
  private hashOverrides: Record<string, string> = {};
  private macroStepRequests: Array<Record<string, MacroStepCaptureSpec>> = [];
  // Production-readiness Phase 5 (R13): the instance target each request was
  // called with (undefined = legacy aggregate path), parallel to
  // `macroStepRequests`.
  private macroStepRequestTargets: Array<MacroStepTarget | undefined> = [];
  private macroStepFailureMode: 'none' | 'reject_immediately' | 'never_reply' = 'none';

  constructor() {
    console.log('[MockPlayjectorService] 🎮 Mock Playjector service initialized');
    console.log('[MockPlayjectorService] ⚙️  Settings:', {
      autoLaunch: MOCK_SETTINGS.launch.autoLaunch,
      launchDelayMs: MOCK_SETTINGS.launch.launchDelayMs,
      timeoutMs: MOCK_SETTINGS.launch.timeoutMs,
      shouldSucceed: MOCK_SETTINGS.launch.shouldSucceed,
      autoCloseDurationMs: MOCK_SETTINGS.session.autoCloseDurationMs,
    });
    console.log('[MockPlayjectorService] 💡 Use mockGameLaunch(), mockSteamLogin() to manually simulate');
    console.log('[MockPlayjectorService] 💡 Change settings: window.__mockPlayjector.updateSettings()');
  }

  /**
   * Reset mock state (useful for tests)
   */
  public reset(): void {
    // Clear any pending timers
    if (this.gameCloseTimer) {
      clearTimeout(this.gameCloseTimer);
      this.gameCloseTimer = null;
    }
    if (this.launchTimer) {
      clearTimeout(this.launchTimer);
      this.launchTimer = null;
    }
    if (this.launchTimeoutTimer) {
      clearTimeout(this.launchTimeoutTimer);
      this.launchTimeoutTimer = null;
    }

    // Reset state
    this.state = {
      gameLaunched: false,
      gameName: '',
      gameId: 0,
      steamLaunched: false,
      steamWindow: false,
      userLoggedIn: false,
    };

    // Clear listeners
    this.listeners = [];
    this.launchResultListeners = [];
    this.lastCredentials = null;
    this.pendingCampaignId = null;

    // Reset macro step bookkeeping
    if (this.pendingMacroStep) {
      this.pendingMacroStep.reject(new Error('MockPlayjectorService reset'));
      this.pendingMacroStep = null;
    }
    this.macroStepRequests = [];
    this.hashOverrides = {};
    this.macroStepFailureMode = 'none';

    // Reset android multi-instance bookkeeping (Phase 3)
    this.instanceLaunchedListeners = [];
    this.instanceClosedListeners = [];
    this.androidLaunchPackages.length = 0;
    this.androidRestartCalls.length = 0;
    this.androidInstanceBehaviors = {};
    this.registeredAndroidStreamIds.clear();

    console.log('[MockPlayjectorService] 🔄 State reset');
  }


  isAvailable(): boolean {
    return true; // Mock is always "available"
  }

  sendHostModeChanged(
    edition: PlaycastHostEdition,
    matchmakeable: boolean,
    steamGuestLogin: boolean,
    guestAccount: boolean,
    credentials: string,
    hostAuthId: string,
    campaignId?: string,
  ): void {
    console.log('[MockPlayjectorService] 📤 sendHostModeChanged (MOCKED)', {
      edition,
      matchmakeable,
      steamGuestLogin,
      guestAccount,
      credentials: credentials ? '***REDACTED***' : '',
      hostAuthId,
      campaignId,
    });

    // Parse credentials (format: "username password")
    if (credentials) {
      const [username, password] = credentials.split(' ');
      this.lastCredentials = { username, password };

      if (MOCK_SETTINGS.logging.verbose) {
        console.log('[MockPlayjectorService] 🔑 Steam credentials received:', {
          username,
          password: password ? '***' : undefined,
        });
      }

      // Auto-simulate Steam login after receiving credentials
      // This mimics Playjector's behavior: it receives credentials and logs into Steam
      if (MOCK_SETTINGS.steam.autoLogin) {
        setTimeout(() => {
          // Check if login should fail
          const shouldFail = !MOCK_SETTINGS.steam.shouldSucceed || this.shouldSimulateError('steam_failure');

          if (shouldFail) {
            console.error('[MockPlayjectorService] ❌ Steam login failed (simulated)');
            // Keep userLoggedIn = false (no login)
            return;
          }

          if (MOCK_SETTINGS.logging.verbose) {
            console.log('[MockPlayjectorService] 🎮 Auto-simulating Steam login...');
          }
          this.mockSteamLogin();
        }, MOCK_SETTINGS.steam.loginDelayMs);
      }
    }

    // NOTE: Game launch is now handled by launchCampaign() method
    // Real Playjector receives hostModeChanged (for credentials/mode) and processLaunch (to actually launch)
  }

  killPlayjector(): void {
    console.log('[MockPlayjectorService] 💀 killPlayjector (MOCKED)');
    this.mockGameClose();
    this.mockSteamLogout();
  }

  terminateGame(_hostAuthId: string): void {
    console.log('[MockPlayjectorService] 🛑 terminateGame (MOCKED) — stopping game only');
    this.mockGameClose();
  }

  connectPlayjector(): void {
    console.log('[MockPlayjectorService] 🔌 connectPlayjector (MOCKED)');
  }

  onStateChange(callback: (state: PlayjectorState) => void): () => void {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  onCampaignLaunchResult(callback: (result: CampaignLaunchResult) => void): () => void {
    this.launchResultListeners.push(callback);
    return () => {
      const index = this.launchResultListeners.indexOf(callback);
      if (index > -1) {
        this.launchResultListeners.splice(index, 1);
      }
    };
  }

  getCurrentState(): PlayjectorState {
    return { ...this.state };
  }

  /**
   * Notify listeners of a campaign launch result
   * Called by IPC handler when pavilionLaunchReply is received
   */
  notifyCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[MockPlayjectorService] 📨 Notifying listeners of campaign launch result:', result);
    this.notifyLaunchResultListeners(result);
  }

  // ========================================
  // Stateful macro step (Phase 3)
  // ========================================

  public async requestMacroStep(
    captures: Record<string, MacroStepCaptureSpec>,
    _screenshotName?: string,
    target?: MacroStepTarget,
  ): Promise<Record<string, MacroStepCaptureResult>> {
    if (!target && this.pendingMacroStep) {
      throw new Error('A macro step request is already in flight');
    }
    if (Object.keys(captures).length === 0) {
      throw new Error('requestMacroStep called with empty captures map');
    }

    this.macroStepRequests.push(captures);
    this.macroStepRequestTargets.push(target);
    console.log('[MockPlayjectorService] 📤 requestMacroStep (MOCKED)', Object.keys(captures), target ?? '(aggregate)');

    if (this.macroStepFailureMode === 'reject_immediately') {
      throw new Error('Mock macro step failure (reject_immediately)');
    }

    if (target) {
      // Instance-targeted path (production-readiness Phase 5, R13): keyed
      // requests run concurrently — each resolves independently on the next
      // tick instead of contending on the single legacy pending slot.
      // Phase 9 failure-injection caveat (Phase 8 review nit N10): targeted
      // calls auto-resolve and have NO equivalent of the real service's 10s
      // reply timeout — 'never_reply' pends the returned promise forever.
      return new Promise<Record<string, MacroStepCaptureResult>>((resolve) => {
        if (this.macroStepFailureMode === 'never_reply') {
          return;
        }
        setTimeout(() => resolve(this.buildMacroStepResults(captures)), 0);
      });
    }

    return new Promise<Record<string, MacroStepCaptureResult>>((resolve, reject) => {
      this.pendingMacroStep = { resolve, reject, captures };

      if (this.macroStepFailureMode === 'never_reply') {
        // Caller is responsible for cleanup; mock test code that uses this mode
        // typically advances jest fake timers to trigger the host-side timeout.
        return;
      }

      // Auto-resolve on the next tick with a synthesized hash per captureId.
      // Tests that need precise control should override behavior via
      // `setMacroStepResolver` or call `notifyMacroStepResult` directly.
      setTimeout(() => this.autoResolveMacroStep(), 0);
    });
  }

  public notifyMacroStepResult(
    reply: MacroStepReply & { error?: string; streamId?: string; gameInstanceIndex?: number },
  ): void {
    const waiter = this.pendingMacroStep;
    if (!waiter) {
      console.warn('[MockPlayjectorService] notifyMacroStepResult called with no pending request');
      return;
    }
    this.pendingMacroStep = null;
    if (reply.error) {
      waiter.reject(new Error(`Mock macro step error: ${reply.error}`));
      return;
    }
    waiter.resolve(reply.variantResults);
  }

  public abandonMacroStep(): void {
    const waiter = this.pendingMacroStep;
    if (!waiter) {
      return;
    }
    this.pendingMacroStep = null;
    waiter.reject(new Error('Macro step abandoned'));
  }

  /**
   * Test helper: register a hash override for a captureId. Subsequent
   * `requestMacroStep` calls that include the captureId will resolve with this
   * hash instead of the synthesized one.
   */
  public setMacroStepHash(captureId: string, hash: string): void {
    this.hashOverrides[captureId] = hash;
  }

  /**
   * Test helper: control macro-step failure behavior.
   *  - 'none' (default): auto-resolve with synthesized hashes.
   *  - 'reject_immediately': throw inside `requestMacroStep` before returning.
   *  - 'never_reply': leave the promise pending forever.
   */
  public setMacroStepFailureMode(mode: 'none' | 'reject_immediately' | 'never_reply'): void {
    this.macroStepFailureMode = mode;
  }

  /**
   * Test helper: snapshot of all `requestMacroStep` calls received so far.
   */
  public getMacroStepRequests(): ReadonlyArray<Record<string, MacroStepCaptureSpec>> {
    return this.macroStepRequests;
  }

  /**
   * Test helper (production-readiness Phase 5, R13): the instance target each
   * `requestMacroStep` call received, parallel to `getMacroStepRequests()`
   * (undefined = legacy aggregate path).
   */
  public getMacroStepRequestTargets(): ReadonlyArray<MacroStepTarget | undefined> {
    return this.macroStepRequestTargets;
  }

  private buildMacroStepResults(
    captures: Record<string, MacroStepCaptureSpec>,
  ): Record<string, MacroStepCaptureResult> {
    const variantResults: Record<string, MacroStepCaptureResult> = {};
    for (const [captureId, spec] of Object.entries(captures)) {
      variantResults[captureId] = {
        hash: this.hashOverrides[captureId] ?? synthesizeMockHash(captureId),
        width: spec.width,
        height: spec.height,
      };
    }
    return variantResults;
  }

  private autoResolveMacroStep(): void {
    const waiter = this.pendingMacroStep;
    if (!waiter) return;
    this.notifyMacroStepResult({ variantResults: this.buildMacroStepResults(waiter.captures) });
  }

  // ========================================
  // Scheduled screenshot (watcher system)
  // ========================================

  private pendingScheduledScreenshot: {
    resolve(value: ScheduledScreenshotResult): void;
    reject(reason: Error): void;
    spec: ScheduledScreenshotSpec;
  } | null = null;
  private scheduledScreenshotHash: string | null = null;

  public async requestScheduledScreenshot(
    spec: ScheduledScreenshotSpec,
  ): Promise<ScheduledScreenshotResult> {
    if (this.pendingScheduledScreenshot) {
      throw new Error('A scheduled screenshot request is already in flight');
    }

    console.log('[MockPlayjectorService] 📤 requestScheduledScreenshot (MOCKED)', spec);

    return new Promise<ScheduledScreenshotResult>((resolve, reject) => {
      this.pendingScheduledScreenshot = { resolve, reject, spec };
      setTimeout(() => this.autoResolveScheduledScreenshot(), 0);
    });
  }

  public notifyScheduledScreenshotResult(
    reply: ScheduledScreenshotResult & { error?: string },
  ): void {
    const waiter = this.pendingScheduledScreenshot;
    if (!waiter) {
      console.warn('[MockPlayjectorService] notifyScheduledScreenshotResult called with no pending request');
      return;
    }
    this.pendingScheduledScreenshot = null;
    if (reply.error) {
      waiter.reject(new Error(`Mock scheduled screenshot error: ${reply.error}`));
      return;
    }
    waiter.resolve({ hash: reply.hash, width: reply.width, height: reply.height });
  }

  /**
   * Test helper: set the hash that will be returned by the next scheduled
   * screenshot request. Resets to synthesized hash after use.
   */
  public setScheduledScreenshotHash(hash: string): void {
    this.scheduledScreenshotHash = hash;
  }

  // ========================================
  // Android multi-instance (implemented Phase 2: capabilities; Phase 3: launch)
  // ========================================

  /**
   * Dev default: pretend the machine is fully provisioned (9 VMs, ceiling 9)
   * so the dev harness never clamps launches to 0. Override with
   * `setInstanceCapabilities` to exercise clamp/report paths.
   */
  private instanceCapabilities: InstanceCapabilities = {
    provisionedInstanceCount: 9,
    maxGameInstances: 9,
  };

  public async getInstanceCapabilities(): Promise<InstanceCapabilities> {
    return { ...this.instanceCapabilities };
  }

  /** Test/dev helper: override the capabilities the mock reports. */
  public setInstanceCapabilities(capabilities: InstanceCapabilities): void {
    this.instanceCapabilities = capabilities;
  }

  // ----- Android instance launch (Phase 3) -----
  //
  // Default behavior: `launchAndroidInstances` records the package and emits a
  // success `instanceLaunched` event per expected instance on the next tick
  // (async, like the real per-instance replies). Tests/dev can override
  // per-instance behavior via `setAndroidInstanceBehavior`:
  //  - 'success' (default): reply { result: 'success', shadowCamName echo }
  //  - 'error':             reply { result: 'error', error: ... }
  //  - 'silent':            never reply — exercises the host reply timeout.
  private instanceLaunchedListeners: Array<(event: InstanceLaunchedEvent) => void> = [];
  private instanceClosedListeners: Array<(event: InstanceClosedEvent) => void> = [];
  private readonly androidLaunchPackages: ProcessLaunchPackageAndroid[] = [];
  private readonly androidRestartCalls: Array<{ streamId: string; gameInstanceIndex: number }> = [];
  private androidInstanceBehaviors: Record<number, 'success' | 'error' | 'silent'> = {};
  // Registration parity with PlayjectorService (Phase 8, review fix S1): the
  // mock does not gate its simulate* helpers on this set — dev/test code emits
  // deliberately — but tracks it so cleanup-path unregistration is observable.
  private readonly registeredAndroidStreamIds = new Set<string>();

  public async launchAndroidInstances(pkg: ProcessLaunchPackageAndroid): Promise<void> {
    console.log('[MockPlayjectorService] 🎮 launchAndroidInstances (MOCKED)', {
      streamId: pkg.streamId,
      package: pkg.package,
      instanceCount: pkg.instanceCount,
    });
    this.androidLaunchPackages.push(pkg);
    this.registeredAndroidStreamIds.add(pkg.streamId);
    for (let gameInstanceIndex = 0; gameInstanceIndex < pkg.instanceCount; gameInstanceIndex += 1) {
      const behavior = this.androidInstanceBehaviors[gameInstanceIndex] ?? 'success';
      if (behavior === 'silent') continue;
      const event: InstanceLaunchedEvent = behavior === 'success'
        ? {
          streamId: pkg.streamId,
          gameInstanceIndex,
          // Frozen camera naming contract (C++ ShadowCamName): instance k maps
          // to ShadowCam{k+1:02d}. Inlined — the mock must not import
          // coordinator code.
          shadowCamName: `ShadowCam${String(gameInstanceIndex + 1).padStart(2, '0')}`,
          result: 'success',
        }
        : {
          streamId: pkg.streamId,
          gameInstanceIndex,
          shadowCamName: '',
          result: 'error',
          error: 'Mock android instance launch failure',
        };
      setTimeout(() => this.emitInstanceLaunched(event), 0);
    }
  }

  public async restartInstance(streamId: string, gameInstanceIndex: number): Promise<string> {
    console.log('[MockPlayjectorService] 🔄 restartInstance (MOCKED)', { streamId, gameInstanceIndex });
    this.androidRestartCalls.push({ streamId, gameInstanceIndex });
    // Dev-harness parity with the real flow (Phase 8, review fix N6): a
    // successful restart is followed by the relaunched instance's
    // `instanceLaunched` echo, so the recovery policy's echo wait settles
    // through the production composite routing (B1) instead of timing out.
    setTimeout(() => this.simulateInstanceLaunched({
      streamId,
      gameInstanceIndex,
      // Frozen camera naming contract, inlined as in launchAndroidInstances.
      shadowCamName: `ShadowCam${String(gameInstanceIndex + 1).padStart(2, '0')}`,
      result: 'success',
    }), 0);
    return 'success';
  }

  /** Phase 8 (review fix S1): drop a torn-down stream from the registered set. */
  public unregisterAndroidStream(streamId: string): void {
    this.registeredAndroidStreamIds.delete(streamId);
  }

  /** Test helper: stream ids currently registered for reply routing. */
  public getRegisteredAndroidStreamIds(): readonly string[] {
    return [...this.registeredAndroidStreamIds];
  }

  public onInstanceLaunched(callback: (event: InstanceLaunchedEvent) => void): () => void {
    this.instanceLaunchedListeners.push(callback);
    return () => {
      const index = this.instanceLaunchedListeners.indexOf(callback);
      if (index > -1) this.instanceLaunchedListeners.splice(index, 1);
    };
  }

  public onInstanceClosed(callback: (event: InstanceClosedEvent) => void): () => void {
    this.instanceClosedListeners.push(callback);
    return () => {
      const index = this.instanceClosedListeners.indexOf(callback);
      if (index > -1) this.instanceClosedListeners.splice(index, 1);
    };
  }

  /** Test helper: android launch packages received so far. */
  public getAndroidLaunchPackages(): readonly ProcessLaunchPackageAndroid[] {
    return this.androidLaunchPackages;
  }

  /** Test helper: restart calls received so far. */
  public getAndroidRestartCalls(): ReadonlyArray<{ streamId: string; gameInstanceIndex: number }> {
    return this.androidRestartCalls;
  }

  /** Test helper: set the per-instance launch behavior for one instance index. */
  public setAndroidInstanceBehavior(
    gameInstanceIndex: number,
    behavior: 'success' | 'error' | 'silent',
  ): void {
    this.androidInstanceBehaviors[gameInstanceIndex] = behavior;
  }

  /** Test helper: emit an instanceLaunched event out of band. */
  public simulateInstanceLaunched(event: InstanceLaunchedEvent): void {
    this.emitInstanceLaunched(event);
  }

  /** Test helper: emit an instanceClosed event out of band. */
  public simulateInstanceClosed(event: InstanceClosedEvent): void {
    this.instanceClosedListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('[MockPlayjectorService] Instance closed listener error:', error);
      }
    });
  }

  private emitInstanceLaunched(event: InstanceLaunchedEvent): void {
    this.instanceLaunchedListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('[MockPlayjectorService] Instance launched listener error:', error);
      }
    });
  }

  private autoResolveScheduledScreenshot(): void {
    const waiter = this.pendingScheduledScreenshot;
    if (!waiter) return;
    const hash = this.scheduledScreenshotHash ?? synthesizeMockHash('scheduled');
    this.scheduledScreenshotHash = null;
    this.notifyScheduledScreenshotResult({
      hash,
      width: waiter.spec.width,
      height: waiter.spec.height,
    });
  }

  /**
   * Launch a campaign (mock implementation)
   *
   * This simulates Playjector's internal behavior when it receives processLaunch IPC:
   * 1. Receives processLaunch with launchPackage
   * 2. Starts internal timeout for Steam launch failures
   * 3. Terminates old game if running
   * 4. Launches new game via Steam
   * 5. Waits for game to launch
   * 6. Sends pavilionLaunchReply (SUCCESS or FAILURE)
   *
   * The real Playjector has these timeouts and auto-behaviors built-in.
   * We mimic them here so tests can verify the full flow.
   */
  launchCampaign(hostAuthId: string, edition: PlaycastHostEdition, launchPackage: any): void {
    console.log('[MockPlayjectorService] 🎮 processLaunch IPC received (MOCKED):', {
      edition,
      launcher: launchPackage.launcher,
      launchId: launchPackage.launchId,
      autoLaunch: MOCK_SETTINGS.launch.autoLaunch,
    });

    // Extract campaign ID from launchId (the Steam app ID or game identifier)
    const campaignId = launchPackage.launchId || 'unknown-campaign';

    // Store pending campaign for timeout tracking
    this.pendingCampaignId = campaignId;

    // PLAYJECTOR INTERNAL BEHAVIOR: Start timeout for Steam launch failures
    // Real Playjector has built-in timeout (~60s) for when Steam fails to launch the game
    if (MOCK_SETTINGS.launch.timeoutMs > 0) {
      // Clear any existing timeout timer
      if (this.launchTimeoutTimer) {
        clearTimeout(this.launchTimeoutTimer);
      }

      // Start timeout timer (simulates Playjector's internal timeout)
      this.launchTimeoutTimer = setTimeout(() => {
        console.error(`[MockPlayjectorService] ⏰ TIMEOUT: Game launch timed out after ${MOCK_SETTINGS.launch.timeoutMs}ms`);
        console.error(`[MockPlayjectorService] 📤 Sending pavilionLaunchReply FAILURE (Playjector internal timeout)`);

        if (!this.pendingCampaignId) {
          console.error('[MockPlayjectorService] ❌ Timeout fired but no pending campaign');
          return;
        }

        const result: CampaignLaunchResult = {
          result: 'FAILURE',
          campaignId: this.pendingCampaignId,
          message: 'Game launch timeout - Steam failed to launch',
        };

        this.notifyLaunchResultListeners(result);
        this.pendingCampaignId = null;
        this.launchTimeoutTimer = null;
      }, MOCK_SETTINGS.launch.timeoutMs);

      console.log(`[MockPlayjectorService] ⏱️  Playjector internal timeout started: ${MOCK_SETTINGS.launch.timeoutMs}ms`);
    }

    // PLAYJECTOR INTERNAL BEHAVIOR: Auto-launch game
    // Real Playjector automatically tries to launch the game when it receives processLaunch
    // If a game is already running, Playjector terminates it first before launching the new one
    if (MOCK_SETTINGS.launch.autoLaunch) {
      // Terminate existing game if running (simulates Playjector's internal process management)
      if (this.state.gameLaunched) {
        console.log('[MockPlayjectorService] 🔄 Game already running — terminating before relaunch');
        this.mockGameClose();
      }

      setTimeout(() => {
        // Resolve game info from launchPackage
        const gameInfo = this.resolveGameFromLaunchPackage(launchPackage);

        if (!gameInfo) {
          console.error('[MockPlayjectorService] ❌ Could not resolve game from launch package');

          // Clear timeout timer
          if (this.launchTimeoutTimer) {
            clearTimeout(this.launchTimeoutTimer);
            this.launchTimeoutTimer = null;
          }

          const result: CampaignLaunchResult = {
            result: 'FAILURE',
            campaignId,
            message: 'Invalid launch package - could not resolve game',
          };
          this.notifyLaunchResultListeners(result);
          this.pendingCampaignId = null;
          return;
        }

        console.log(`[MockPlayjectorService] 🚀 Playjector launching game:`, gameInfo);

        // Actually launch the game if it should succeed
        if (MOCK_SETTINGS.launch.shouldSucceed) {
          this.mockGameLaunch(gameInfo.gameId, gameInfo.gameName);
        }

        // Emit launch result after delay (simulating pavilionLaunchReply from Playjector)
        const resultDelayMs = MOCK_SETTINGS.launch.launchDelayMs || 1000;
        setTimeout(() => {
          // Clear timeout timer if game launched successfully
          if (this.launchTimeoutTimer && MOCK_SETTINGS.launch.shouldSucceed) {
            clearTimeout(this.launchTimeoutTimer);
            this.launchTimeoutTimer = null;
            console.log('[MockPlayjectorService] ✅ Cleared Playjector timeout (game launched)');
          }

          const result: CampaignLaunchResult = MOCK_SETTINGS.launch.shouldSucceed
            ? {
                result: 'SUCCESS',
                campaignId,
                gameId: gameInfo.gameId,
                gameName: gameInfo.gameName,
                message: 'Game launched successfully',
              }
            : {
                result: 'FAILURE',
                campaignId,
                message: MOCK_SETTINGS.launch.failureReason || 'Simulated launch failure',
              };

          console.log('[MockPlayjectorService] 📤 Sending pavilionLaunchReply (MOCKED):', result.result);
          this.notifyLaunchResultListeners(result);
          this.pendingCampaignId = null;
        }, resultDelayMs);
      }, MOCK_SETTINGS.launch.launchDelayMs);
    } else {
      console.log('[MockPlayjectorService] 🚫 Auto-launch disabled - game will not launch automatically');
    }
  }

  /**
   * Resolve game info from launch package
   * @private
   */
  private resolveGameFromLaunchPackage(launchPackage: any): { gameId: number; gameName: string } | null {
    // Campaign-to-game mapping database
    const gameDatabase: Record<string, { gameId: number; gameName: string }> = {
      'cs2': { gameId: 730, gameName: 'Counter-Strike 2' },
      'csgo': { gameId: 730, gameName: 'Counter-Strike 2' },
      'counterstrike': { gameId: 730, gameName: 'Counter-Strike 2' },
      '730': { gameId: 730, gameName: 'Counter-Strike 2' },
      'fortnite': { gameId: 1172470, gameName: 'Fortnite' },
      'apex': { gameId: 1172470, gameName: 'Apex Legends' },
      'valorant': { gameId: 1517290, gameName: 'Valorant' },
      'dota2': { gameId: 570, gameName: 'Dota 2' },
      'dota': { gameId: 570, gameName: 'Dota 2' },
      '570': { gameId: 570, gameName: 'Dota 2' },
      'tf2': { gameId: 440, gameName: 'Team Fortress 2' },
      '440': { gameId: 440, gameName: 'Team Fortress 2' },
      'pubg': { gameId: 578080, gameName: 'PUBG' },
    };

    const launchId = launchPackage.launchId || '';

    // Method 1: Direct match by launchId
    let game = gameDatabase[launchId.toLowerCase()];

    // Method 2: Check if launchId contains a known game name
    if (!game) {
      const launchIdLower = launchId.toLowerCase();
      for (const [key, gameData] of Object.entries(gameDatabase)) {
        if (launchIdLower.includes(key)) {
          game = gameData;
          break;
        }
      }
    }

    // Method 3: Default to CS2 for testing
    if (!game) {
      console.warn(`[MockPlayjectorService] ⚠️  Unknown launchId: "${launchId}" - defaulting to CS2`);
      game = gameDatabase['cs2'];
    }

    return game;
  }

  // ========================================
  // Mock Control Methods (for testing/debugging)
  // ========================================

  /**
   * Simulate a game launch
   *
   * @example
   * mockPlayjectorService.mockGameLaunch(730, 'Counter-Strike 2');
   */
  public mockGameLaunch(gameId: number, gameName: string): void {
    console.log(`[MockPlayjectorService] 🎮 Simulating game launch: ${gameName} (${gameId})`);

    this.state = {
      ...this.state,
      gameLaunched: true,
      gameId,
      gameName,
      steamLaunched: true,
      steamWindow: false, // Game window, not Steam window
      userLoggedIn: true, // User is logged in when game launches
    };

    this.notifyListeners();

    // Auto-close game after configured duration (for crash simulation)
    if (MOCK_SETTINGS.session.autoCloseDurationMs > 0) {
      this.gameCloseTimer = setTimeout(() => {
        console.log(`[MockPlayjectorService] ⏰ Auto-closing game after ${MOCK_SETTINGS.session.autoCloseDurationMs}ms (crash simulation)`);
        this.mockGameClose();
      }, MOCK_SETTINGS.session.autoCloseDurationMs);
    }
  }

  /**
   * Simulate a game close
   */
  public mockGameClose(): void {
    console.log('[MockPlayjectorService] 🛑 Simulating game close');

    // Clear auto-close timer
    if (this.gameCloseTimer) {
      clearTimeout(this.gameCloseTimer);
      this.gameCloseTimer = null;
    }

    this.state = {
      ...this.state,
      gameLaunched: false,
      gameId: 0,
      gameName: '',
      steamLaunched: false,
      steamWindow: false,
    };

    this.notifyListeners();
  }

  /**
   * Simulate Steam login
   */
  public mockSteamLogin(): void {
    console.log('[MockPlayjectorService] ✅ Simulating Steam login');

    this.state = {
      ...this.state,
      userLoggedIn: true,
    };

    this.notifyListeners();
  }

  /**
   * Simulate Steam logout
   */
  public mockSteamLogout(): void {
    console.log('[MockPlayjectorService] ❌ Simulating Steam logout');

    this.state = {
      ...this.state,
      userLoggedIn: false,
    };

    this.notifyListeners();
  }

  /**
   * Simulate Steam window visibility change
   */
  public mockSteamWindow(visible: boolean): void {
    console.log(`[MockPlayjectorService] 🪟 Simulating Steam window ${visible ? 'visible' : 'hidden'}`);

    this.state = {
      ...this.state,
      steamWindow: visible,
    };

    this.notifyListeners();
  }

  /**
   * Get the last credentials sent to the service (for testing)
   */
  public getLastCredentials(): SteamCredentials | null {
    return this.lastCredentials;
  }

  /**
   * Simulate a campaign launch result (for testing)
   *
   * @example
   * mockPlayjectorService.simulateCampaignLaunchResult({
   *   result: 'SUCCESS',
   *   campaignId: 'campaign-cs2-123',
   *   gameId: 730,
   *   gameName: 'Counter-Strike 2',
   * });
   */
  public simulateCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[MockPlayjectorService] 📢 Simulating campaign launch result:', result);
    this.notifyLaunchResultListeners(result);
  }

  /**
   * Check if an error should be simulated
   * @private
   */
  private shouldSimulateError(errorType: 'launch_failure' | 'steam_failure' | 'crash' | 'disconnect'): boolean {
    if (!MOCK_SETTINGS.errors.enabled) return false;
    if (!MOCK_SETTINGS.errors.types.includes(errorType)) return false;
    return Math.random() < MOCK_SETTINGS.errors.errorRate;
  }

  /**
   * Simulate a launch failure (matches real Playjector error behavior)
   * @private
   */
  private simulateLaunchFailure(reason: string): void {
    console.error('[MockPlayjectorService] ❌ Launch failed:', reason);
    // Mock doesn't send IPC - it only updates state
    // Coordinator listens to state changes and reacts accordingly
  }

  /**
   * Update mock settings at runtime
   */
  public updateSettings(newSettings: Partial<MockPlayjectorSettings>): void {
    updateMockSettings(newSettings);
    console.log('[MockPlayjectorService] Settings updated');
  }

  /**
   * Reset settings to defaults
   */
  public resetSettings(): void {
    resetMockSettings();
    console.log('[MockPlayjectorService] Settings reset to defaults');
  }

  /**
   * Get current settings
   */
  public getSettings(): MockPlayjectorSettings {
    return { ...MOCK_SETTINGS };
  }

  // ========================================
  // Recording control (Phase 3)
  // ========================================

  private recordingControlAutoResolve = true;
  private recordingSessionScreenshotIndex = 0;

  public async startRecording(takeId: string, cameraName?: string): Promise<void> {
    console.log('[MockPlayjectorService] 📤 startRecording (MOCKED)', { takeId, cameraName });
    if (!this.recordingControlAutoResolve) {
      throw new Error('Mock recording start failure');
    }
  }

  public async stopRecording(takeId: string): Promise<RecordingStopResult> {
    console.log('[MockPlayjectorService] 📤 stopRecording (MOCKED)', { takeId });
    if (!this.recordingControlAutoResolve) {
      throw new Error('Mock recording stop failure');
    }
    return { filePath: `C:\\ProgramData\\Playcast\\recordings\\${takeId}.mp4` };
  }

  public notifyRecordControlStatus(_reply: {
    status: string;
    takeId: string;
    filePath?: string;
    error?: string;
  }): void {
    console.warn('[MockPlayjectorService] notifyRecordControlStatus — no-op in mock');
  }

  public async waitForEncoderReady(takeId: string): Promise<void> {
    // Mock encoders start instantly — resolve immediately so admin-side flows
    // gated on encoder readiness never stall in dev/test.
    console.log('[MockPlayjectorService] 📤 waitForEncoderReady (MOCKED) — resolving immediately', { takeId });
  }

  public notifyEncoderStatus(_reply: { cameraName?: string; result: string }): void {
    // No-op: the mock never leaves a waiter pending.
  }

  public async captureSessionScreenshot(
    sessionName: string,
    width: number,
    height: number,
  ): Promise<SessionScreenshotResult> {
    console.log('[MockPlayjectorService] 📤 captureSessionScreenshot (MOCKED)', { sessionName, width, height });
    const index = this.recordingSessionScreenshotIndex++;
    return {
      index,
      filePath: `C:\\ProgramData\\Playcast\\screenshots\\${sessionName}\\${index}.png`,
      timestampMs: Date.now(),
    };
  }

  public notifySessionScreenshotResult(_reply: {
    result: string;
    sessionName: string;
    index: number;
    filePath: string;
    timestampMs: number;
    error?: string;
  }): void {
    console.warn('[MockPlayjectorService] notifySessionScreenshotResult — no-op in mock');
  }

  /**
   * Test helper: control recording auto-resolve behavior
   */
  public setRecordingControlAutoResolve(autoResolve: boolean): void {
    this.recordingControlAutoResolve = autoResolve;
  }

  /**
   * Test helper: reset session screenshot index
   */
  public resetSessionScreenshotIndex(): void {
    this.recordingSessionScreenshotIndex = 0;
  }

  // ========================================
  // Test-friendly aliases (for test files)
  // ========================================

  /**
   * Simulate game launch (supports both direct call and launchPackage)
   *
   * @param gameIdOrPackage - Either a gameId number, or a launchPackage object
   * @param gameName - Game name (only if first param is gameId)
   */
  public simulateGameLaunch(gameIdOrPackage: number | any, gameName?: string): void {
    // Overload 1: Called with (gameId, gameName)
    if (typeof gameIdOrPackage === 'number' && gameName) {
      this.mockGameLaunch(gameIdOrPackage, gameName);
      return;
    }

    // Overload 2: Called with launchPackage object
    const launchPackage = gameIdOrPackage;
    const gameInfo = this.resolveGameFromLaunchPackage(launchPackage);
    if (gameInfo) {
      this.mockGameLaunch(gameInfo.gameId, gameInfo.gameName);
    }
  }

  /**
   * Alias for mockGameClose (test-friendly naming)
   */
  public simulateGameClose(): void {
    this.mockGameClose();
  }

  /**
   * Alias for mockSteamLogin (test-friendly naming)
   */
  public simulateSteamLogin(loggedIn: boolean): void {
    if (loggedIn) {
      this.mockSteamLogin();
    } else {
      this.mockSteamLogout();
    }
  }

  /**
   * Alias for mockSteamWindow (test-friendly naming)
   */
  public simulateSteamWindow(visible: boolean): void {
    this.mockSteamWindow(visible);
  }

  // ========================================
  // Private Helpers
  // ========================================

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('[MockPlayjectorService] Listener error:', error);
      }
    });
  }

  private notifyLaunchResultListeners(result: CampaignLaunchResult): void {
    console.log('[MockPlayjectorService] 📢 Emitting campaign launch result:', result);
    this.launchResultListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (error) {
        console.error('[MockPlayjectorService] Launch result listener error:', error);
      }
    });
  }
}

// ========================================
// Global Mock Instance (for console access)
// ========================================

/**
 * Expose mock service to window for easy testing in browser console
 *
 * Usage:
 * ```javascript
 * // Manually launch a game
 * window.__mockPlayjector.launchGame(730, 'CS:GO');
 *
 * // Close the current game
 * window.__mockPlayjector.closeGame();
 *
 * // Toggle auto-launch
 * window.__mockPlayjector.setAutoLaunch(false);
 *
 * // Check current state
 * window.__mockPlayjector.getState();
 * ```
 */
export function setGlobalMockInstance(instance: MockPlayjectorService): void {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__mockPlayjector = {
      launchGame: (gameId: number, gameName: string) => {
        console.log(`[MockPlayjector Console] Launching ${gameName} (${gameId})`);
        instance.mockGameLaunch(gameId, gameName);
      },
      closeGame: () => {
        console.log('[MockPlayjector Console] Closing game');
        instance.mockGameClose();
      },
      login: () => {
        console.log('[MockPlayjector Console] Logging in to Steam');
        instance.mockSteamLogin();
      },
      logout: () => {
        console.log('[MockPlayjector Console] Logging out of Steam');
        instance.mockSteamLogout();
      },
      updateSettings: (newSettings: Partial<MockPlayjectorSettings>) => {
        console.log('[MockPlayjector Console] Updating settings:', newSettings);
        instance.updateSettings(newSettings);
      },
      resetSettings: () => {
        console.log('[MockPlayjector Console] Resetting settings');
        instance.resetSettings();
      },
      getSettings: () => {
        const settings = instance.getSettings();
        console.log('[MockPlayjector Console] Current settings:', settings);
        return settings;
      },
      getState: () => {
        const state = instance.getCurrentState();
        console.log('[MockPlayjector Console] Current state:', state);
        return state;
      },
      // Helper to quickly test error scenarios
      testErrorScenario: () => {
        console.log('[MockPlayjector Console] Switching to error testing mode');
        instance.updateSettings({
          launch: {
            ...MOCK_SETTINGS.launch,
            shouldSucceed: false,
            failureReason: 'Console test: Simulated failure',
          },
        });
        console.log('[MockPlayjector Console] Next campaign switch will fail!');
      },
      // Helper to quickly test timeouts
      testTimeoutScenario: () => {
        console.log('[MockPlayjector Console] Switching to timeout testing mode');
        instance.updateSettings({
          launch: {
            ...MOCK_SETTINGS.launch,
            launchDelayMs: 7000,
            timeoutMs: 5000,
          },
        });
        console.log('[MockPlayjector Console] Next campaign switch will timeout!');
      },
    };

    console.log('[MockPlayjectorService] 💡 Global controls available: window.__mockPlayjector');
    console.log('[MockPlayjectorService] 💡 Try: __mockPlayjector.launchGame(730, "CS:GO")');
    console.log('[MockPlayjectorService] 💡 Try: __mockPlayjector.testErrorScenario()');
  }
}
